import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, Contact, CornerDownLeft, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { useRefData } from "../context/RefDataContext";
import { can, canOpenRoute } from "../lib/permissions";
import { MENU } from "../config/navigation";

const MAX_PER_GROUP = 6;
const normalize = (text) => String(text || "").toLocaleLowerCase("it-IT").normalize("NFD").replace(/[̀-ͯ]/g, "");
const matches = (query, ...fields) => fields.some((field) => normalize(field).includes(query));

// Results come only from data this user already has (menu, vehicles and
// customers loaded for the page), filtered by the same permissions as the pages.
export function paletteResults(user, refData, text) {
  const query = normalize(text.trim());
  const pages = MENU.filter((m) => canOpenRoute(user, m.to))
    .map((m) => ({ id: `page:${m.to}`, group: "Pagine", label: m.to === "/utenti" ? "Utenti e permessi" : m.label, to: m.to, icon: m.icon }))
    .filter((item) => !query || matches(query, item.label));
  if (!query) return pages;
  const vehicles = canOpenRoute(user, "/veicoli") ? (refData?.vehicles || [])
    .filter((v) => matches(query, v.plate, v.brand, v.model, v.internal_id))
    .slice(0, MAX_PER_GROUP)
    .map((v) => ({ id: `vehicle:${v.id}`, group: "Veicoli", label: v.plate || "Veicolo", hint: [v.brand, v.model].filter(Boolean).join(" "), to: `/veicoli/${v.id}`, icon: Car })) : [];
  const clients = canOpenRoute(user, "/clienti") && can(user, "customers.private") ? (refData?.assignees || [])
    .filter((a) => a.type === "cliente" && matches(query, a.name))
    .slice(0, MAX_PER_GROUP)
    .map((a) => ({ id: `client:${a.id}`, group: "Clienti", label: a.name, to: `/clienti/${a.id}`, icon: Contact })) : [];
  return [...pages, ...vehicles, ...clients];
}

export function useCommandPaletteShortcut(setOpen) {
  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);
}

export default function CommandPalette({ open, onOpenChange }) {
  const { user } = useAuth();
  const refData = useRefData();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const results = useMemo(() => paletteResults(user, refData, text), [user, refData, text]);

  useEffect(() => { if (open) { setText(""); setActive(0); } }, [open]);
  useEffect(() => { setActive(0); }, [text]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const go = (item) => { if (!item) return; onOpenChange(false); navigate(item.to); };
  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") { event.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (event.key === "ArrowUp") { event.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (event.key === "Enter") { event.preventDefault(); go(results[active]); }
  };

  let lastGroup = null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-xl [&>button:last-child]:hidden">
        <DialogTitle className="sr-only">Cerca nell’app</DialogTitle>
        <DialogDescription className="sr-only">Scrivi il nome di una pagina, una targa o un cliente, poi premi Invio.</DialogDescription>
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Search className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <input
            autoFocus
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Pagina, targa o cliente…"
            role="combobox"
            aria-expanded="true"
            aria-controls="command-palette-list"
            aria-activedescendant={results[active] ? `command-${results[active].id}` : undefined}
            aria-label="Cerca nell’app"
            className="h-14 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
            data-testid="command-palette-input"
          />
          <kbd className="hidden rounded border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground sm:inline">Esc</kbd>
        </div>
        <ul ref={listRef} id="command-palette-list" role="listbox" aria-label="Risultati" className="max-h-[min(60dvh,420px)] overflow-y-auto p-2">
          {results.length === 0 && <li className="px-3 py-10 text-center text-sm text-muted-foreground">Nessun risultato per “{text}”.</li>}
          {results.map((item, index) => {
            const heading = item.group !== lastGroup ? item.group : null;
            lastGroup = item.group;
            const Icon = item.icon;
            const selected = index === active;
            return (
              <React.Fragment key={item.id}>
                {heading && <li role="presentation" className="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{heading}</li>}
                <li
                  id={`command-${item.id}`}
                  role="option"
                  aria-selected={selected}
                  data-index={index}
                  onMouseMove={() => setActive(index)}
                  onClick={() => go(item)}
                  className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm ${selected ? "bg-primary/10 text-foreground" : "text-foreground/90"}`}
                >
                  <span className="icon-tile h-8 w-8 rounded-lg" aria-hidden="true"><Icon className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1 truncate font-medium">{item.label}{item.hint && <span className="ml-2 font-normal text-muted-foreground">{item.hint}</span>}</span>
                  {selected && <CornerDownLeft className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
